import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, CircleNotch, EnvelopeSimple, ArrowRight, WarningCircle } from "@phosphor-icons/react";
import BrandMark from "../components/BrandMark";
import { api } from "../lib/api";

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 30; // 60 seconds total

export default function SignupSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", message: "Confirming your payment with Stripe…" });
  const [resp, setResp] = useState(null);
  const attemptRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", message: "Missing checkout session ID. If you completed checkout, please contact support." });
      return;
    }
    let cancelled = false;
    let timer;

    const poll = async () => {
      attemptRef.current += 1;
      try {
        const r = await api.get(`/billing/checkout/status/${encodeURIComponent(sessionId)}`);
        if (cancelled) return;
        setResp(r.data);
        if (r.data.status === "complete") {
          setState({ phase: "complete", message: "Subscription started. Your account is ready." });
          return;
        }
        if (r.data.status === "expired") {
          setState({ phase: "error", message: "Checkout session expired. Please start a new signup." });
          return;
        }
        if (attemptRef.current >= POLL_MAX_ATTEMPTS) {
          setState({ phase: "timeout", message: "We're still waiting on Stripe. Refresh in a moment, or check your email." });
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        if (attemptRef.current >= 3) {
          setState({ phase: "error", message: "Could not confirm subscription with Stripe. Please contact support if this persists." });
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-white border border-zinc-300" data-testid="signup-success-card">
        <div className="bg-[#1A2332] text-white px-6 py-5 flex items-center gap-3">
          <BrandMark size={36} className="rounded-md" />
          <div>
            <div className="font-display text-lg font-bold leading-none">ezItemizer</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mt-1">
              Account provisioning
            </div>
          </div>
        </div>

        <div className="px-6 py-10 sm:px-8 text-center">
          {state.phase === "polling" && (
            <>
              <CircleNotch size={48} weight="bold" className="text-[#FF9800] mx-auto animate-spin" />
              <h1 className="font-display text-2xl font-bold mt-5 text-[#0F172A]">
                Setting up your workspace
              </h1>
              <p className="mt-3 text-sm text-zinc-600">{state.message}</p>
            </>
          )}

          {state.phase === "complete" && (
            <>
              <CheckCircle size={56} weight="fill" className="text-emerald-600 mx-auto" />
              <h1 className="font-display text-2xl sm:text-3xl font-black mt-5 text-[#0F172A]">
                You're in.
              </h1>
              <p className="mt-3 text-sm sm:text-base text-zinc-600">
                Your 14-day free trial has started and your admin account is ready.
              </p>

              <div className="mt-7 bg-[#FFF8EE] border border-[#FF9800]/30 px-5 py-4 text-left">
                <div className="flex items-start gap-3">
                  <EnvelopeSimple size={20} weight="duotone" className="text-[#FF9800] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-sm text-[#0F172A]">Check your email</div>
                    <div className="text-sm text-zinc-700 mt-1">
                      We just sent your sign-in details to <strong>{resp?.admin_email}</strong>.
                      You'll find your temporary password there — change it after first sign-in.
                    </div>
                  </div>
                </div>
              </div>

              <Link
                to="/login"
                data-testid="signup-success-signin-link"
                className="mt-7 inline-flex items-center justify-center gap-2 bg-[#FF9800] hover:bg-[#E68900] text-[#1A2332] font-bold py-3 px-5 transition-colors active:scale-[0.99]"
              >
                Sign in to ezItemizer
                <ArrowRight size={16} weight="bold" />
              </Link>
            </>
          )}

          {(state.phase === "error" || state.phase === "timeout") && (
            <>
              <WarningCircle size={48} weight="fill" className="text-amber-500 mx-auto" />
              <h1 className="font-display text-2xl font-bold mt-5 text-[#0F172A]">
                {state.phase === "timeout" ? "Still working on it…" : "Something went wrong"}
              </h1>
              <p className="mt-3 text-sm text-zinc-600">{state.message}</p>
              <div className="mt-6 flex items-center justify-center gap-3">
                <button
                  onClick={() => window.location.reload()}
                  className="bg-[#1A2332] hover:bg-[#243044] text-white font-bold text-xs uppercase tracking-widest px-4 py-2.5"
                >
                  Refresh
                </button>
                <Link
                  to="/signup"
                  className="bg-white border border-zinc-300 text-zinc-700 hover:text-[#FF9800] font-bold text-xs uppercase tracking-widest px-4 py-2.5"
                >
                  Start over
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
